/**
 * Money as the database sends it (numeric arrives as a STRING) and as the screen shows it, never through a float
 * (money phase 4, plan R4: "one formatter ... from the string, never through a float"). "1234.5" reads "$1,234.50",
 * "-12" reads "-$12.00". Totals are added in whole cents as BigInt, so 0.10 + 0.20 is exactly 0.30.
 */

const MONEY = /^(-)?(\d+)(?:\.(\d{1,2}))?$/;

/** Whole cents from the database's text, or null when it is not money. */
export function toCents(amount: string | null | undefined): bigint | null {
  const m = MONEY.exec((amount ?? '').trim());
  if (!m) return null;
  const cents = BigInt(m[2]!) * 100n + BigInt((m[3] ?? '').padEnd(2, '0') || '0');
  return m[1] ? -cents : cents;
}

/** "$1,234.56", "-$12.00"; an amount that is not money reads as a dash, never as zero. */
export function formatCents(cents: bigint | null): string {
  if (cents === null) return '—';
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  const dollars = (abs / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const rest = (abs % 100n).toString().padStart(2, '0');
  return `${negative ? '-' : ''}$${dollars}.${rest}`;
}

export function formatMoneyText(amount: string | null | undefined): string {
  return formatCents(toCents(amount));
}

/** A sum of amounts as text, exactly. */
export function sumMoney(amounts: (string | null | undefined)[]): bigint {
  return amounts.reduce<bigint>((s, a) => s + (toCents(a) ?? 0n), 0n);
}

/** Cents back to the database's text ("-12.30"), for a body sent to the API. */
export function centsToText(cents: bigint): string {
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  return `${negative ? '-' : ''}${abs / 100n}.${(abs % 100n).toString().padStart(2, '0')}`;
}

/** The agency's one mask for an account (A-#357): "...1234". Only the last four ever reach the screen. */
export function lastFour(last4: string | null | undefined): string {
  return last4 && /^\d{4}$/.test(last4) ? `...${last4}` : '...';
}
